import { motion } from "framer-motion";
import { AlertOctagon, ArrowDown } from "lucide-react";

/**
 * DashboardRejectionBanner — the very first thing a returning student sees
 * when their draft has been sent back by a reviewer. Louder than the small
 * inline card that used to live inside the campaign builder: full-bleed,
 * scarlet-bordered, the reviewer's exact note in a serif blockquote, and
 * a jump link that scrolls the page to the draft-editor card below.
 *
 * Renders nothing unless status === "rejected" && rejection_reason.
 */
export const DashboardRejectionBanner = ({ status, rejectionReason, reviewedAt }) => {
  if (status !== "rejected" || !rejectionReason) return null;

  const jumpToDraft = () => {
    const el =
      document.querySelector('[data-testid="draft-campaign-card"]') ||
      document.querySelector("#draft");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <motion.section
      role="alert"
      data-testid="dashboard-rejection-banner"
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 0.61, 0.36, 1] }}
      className="relative overflow-hidden rounded-2xl border-2 border-scarlet bg-scarlet/[0.06] p-6 lg:p-8"
    >
      <span
        aria-hidden
        className="pointer-events-none absolute -right-16 -top-16 h-44 w-44 rounded-full bg-scarlet/12 blur-3xl"
      />
      <div className="relative flex items-start gap-5">
        <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-scarlet text-sand shadow-[0_10px_30px_-8px_rgba(196,30,58,0.5)]">
          <AlertOctagon className="h-6 w-6" strokeWidth={2.2} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span
              className="text-[13px] font-semibold uppercase tracking-[0.22em] text-scarlet"
              data-testid="dashboard-rejection-eyebrow"
            >
              Your draft was sent back
            </span>
            {reviewedAt && (
              <span
                className="text-[13px] font-medium uppercase tracking-[0.18em] text-olive"
                data-testid="dashboard-rejection-timestamp"
              >
                {new Date(reviewedAt).toLocaleDateString(undefined, {
                  month: "short",
                  day: "numeric",
                })}
              </span>
            )}
          </div>
          <h2 className="mt-2 font-serif text-3xl leading-[1.05] text-ink">
            One quick round of edits, then <em className="italic text-scarlet">you&rsquo;re live</em>.
          </h2>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-olive">
            Our reviewer flagged something they&rsquo;d like to see changed. Read their exact note below, make the edit on your draft, and resubmit.
          </p>
          <blockquote
            data-testid="dashboard-rejection-reason"
            className="mt-5 rounded-md border-l-4 border-scarlet bg-bone px-5 py-4 font-serif text-3xl leading-relaxed text-ink"
          >
            &ldquo;{rejectionReason}&rdquo;
          </blockquote>
          <button
            type="button"
            onClick={jumpToDraft}
            data-testid="dashboard-rejection-jump"
            className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-ink px-5 py-2.5 text-base font-semibold text-sand transition-colors hover:bg-forest"
          >
            <ArrowDown className="h-4 w-4" strokeWidth={2} /> Take me to my draft
          </button>
        </div>
      </div>
    </motion.section>
  );
};
